import {MouseEvent as ReactMouseEvent, useCallback} from 'react';

import {isString} from 'ramda-adjunct';

import {useBoolean} from 'shared/web';

import {ActionCallback} from '../types/ActionCallback';
import {GridApi} from '../types/AgGridTypes';
import {RowData} from '../types/RowData';
import {getColumnData} from '../utils/getColumnData';
import {refreshDatagrid} from '../utils/refreshDatagrid';
import {getSearchParamsByGridCode} from '../utils/url/getSearchParamsByGridCode';
import {setSourceSearchParamsByGridCode} from '../utils/url/setSourceSearchParamsByGridCode';
import {useDeselectAll} from './useDeselectAll';
import {useHandleLink} from './useHandleLink';
import {useRedrawRows} from './useRedrawRows';
import {useRefreshCells} from './useRefreshCells';

export type ActionCallbackProxy = (
  actionKey: string,
  event: ReactMouseEvent<HTMLButtonElement, MouseEvent>
) => void;

/**
 * Wraps action callback to another function that provieds most of the parameters.
 * We have to provide only actionKey and mouse event.
 */
export const useActionCallbackProxy = (
  actionCallback: ActionCallback<readonly string[]> | undefined,
  gridApi: GridApi,
  rowId: string | string[],
  rowData: RowData,
  gridCode: string
) => {
  const handleLink = useHandleLink();
  const refreshCells = useRefreshCells(gridApi);
  const redrawRows = useRedrawRows(gridApi);
  const deselectAll = useDeselectAll(gridApi);
  const searchParams = getSearchParamsByGridCode(gridCode);

  const [isLoading, startLoading, stopLoading] = useBoolean();

  const actionCallbackProxy = useCallback(
    async (actionKey: string, event?: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!actionCallback) {
        return;
      }

      startLoading();

      const sourceSearchParams = setSourceSearchParamsByGridCode(
        gridCode,
        String(rowData.rowNumber)
      );

      const refreshData = () => refreshDatagrid(gridCode);

      const actionLink = await actionCallback({
        actionKey,
        rowId,
        getColumnData: getColumnData(rowData, gridCode),
        refreshData,
        refreshCells,
        redrawRows,
        rowData,
        deselectAll,
        sourceSearchParams,
        queryId: searchParams?.queryId ?? '',
      });

      if (isString(actionLink) && event) {
        handleLink(actionLink, event.nativeEvent);
      }

      stopLoading();
    },
    [
      actionCallback,
      gridCode,
      rowData,
      startLoading,
      stopLoading,
      rowId,
      refreshCells,
      redrawRows,
      deselectAll,
      searchParams?.queryId,
      handleLink,
    ]
  );

  return [actionCallbackProxy, isLoading] as const;
};
